import React from 'react'
import { Platform } from 'react-native'

export const PRESS_REPEAT_DELAY_MS = 400
export const PRESS_REPEAT_INTERVAL_MS = 80

type PressRepeatHandlers = {
    ref(node: unknown): (() => void) | undefined
    delayLongPress: number
    onPress(): void
    onLongPress(): void
    onPressOut(): void
}

const keepFocusOnMouseDown = (event: Event) => event.preventDefault()

const ref = (node: unknown) => {
    if (Platform.OS !== 'web' || !(node instanceof HTMLElement)) {
        return undefined
    }
    node.addEventListener('mousedown', keepFocusOnMouseDown)
    return () => node.removeEventListener('mousedown', keepFocusOnMouseDown)
}

/**
 * Press-and-hold handlers for stepper controls, spread onto a `Pressable`: a
 * press steps once, holding past `PRESS_REPEAT_DELAY_MS` keeps stepping every
 * `PRESS_REPEAT_INTERVAL_MS` until release. Built on `onPress`/`onLongPress`
 * rather than `onPressIn`, because react-native-web drops `onPressIn` for
 * clicks shorter than its press delay unless `onPress` is also set. The latest
 * `action` is always called, so a controlled parent that re-renders between
 * steps keeps counting from its current value.
 *
 * On web the pressable must not take focus on mouse-down: a modal's focus
 * containment pulls focus back out of a portalled popover, and react-native-web
 * terminates a press whose element blurs, which cancels the long press.
 */
export const usePressRepeat = (action?: () => void): PressRepeatHandlers => {
    const actionRef = React.useRef(action)
    const intervalRef = React.useRef<ReturnType<typeof setInterval>>(undefined)

    React.useLayoutEffect(() => {
        actionRef.current = action
    })

    const onPressOut = React.useCallback(() => {
        clearInterval(intervalRef.current)
    }, [])

    const onPress = React.useCallback(() => {
        actionRef.current?.()
    }, [])

    const onLongPress = React.useCallback(() => {
        onPressOut()
        if (!actionRef.current) {
            return
        }
        actionRef.current()
        intervalRef.current = setInterval(() => actionRef.current?.(), PRESS_REPEAT_INTERVAL_MS)
    }, [onPressOut])

    React.useEffect(() => {
        if (!action) {
            onPressOut()
        }
    }, [action, onPressOut])

    React.useEffect(() => onPressOut, [onPressOut])

    return { ref, delayLongPress: PRESS_REPEAT_DELAY_MS, onPress, onLongPress, onPressOut }
}
